import { ERROR_CODES } from '@inbx/shared/errors';

/**
 * REST API клиент. Bearer-token хранится в memory + localStorage refresh.
 * Workspace slug — из URL (TanStack Router param).
 */

interface ApiError {
  error: { code: string; message: string; details?: unknown };
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<string | null> | null = null;
  /**
   * Встроенный режим: интерфейс открыт внутри кабинета другого сервиса.
   *
   * Токены живут только в памяти вкладки. Класть их в localStorage нельзя:
   * это тот же источник, что и обычная админка, и встроенная сессия
   * (сотрудник, вошедший через чужой кабинет) затирала бы личный вход
   * оператора в соседней вкладке — и наоборот.
   *
   * Соответственно, обновление сессии тоже идёт по токену из памяти, а при
   * его потере вход начинается заново со стороны кабинета, а не с нашей
   * страницы входа: внутри рамки её показывать некому.
   */
  private embedRefreshToken: string | null = null;
  private onEmbedSessionLost: (() => void) | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /** Включить встроенный режим после обмена билета на сессию. */
  setEmbedSession(tokens: { access: string; refresh: string }, onLost: () => void): void {
    this.accessToken = tokens.access;
    this.embedRefreshToken = tokens.refresh;
    this.onEmbedSessionLost = onLost;
  }

  get isEmbedded(): boolean {
    return this.embedRefreshToken !== null;
  }

  async request<T>(
    path: string,
    init: RequestInit = {},
    opts: { workspaceSlug?: string } = {},
  ): Promise<T> {
    const url = path.startsWith('/') ? path : `/${path}`;
    const headers = new Headers(init.headers);
    if (this.accessToken) headers.set('authorization', `Bearer ${this.accessToken}`);
    if (!headers.has('content-type') && init.body) headers.set('content-type', 'application/json');

    const fullUrl = opts.workspaceSlug ? `/api/v1/${opts.workspaceSlug}${url}` : `/api${url}`;

    const res = await fetch(fullUrl, { ...init, headers, credentials: 'include' });

    if (res.status === 401) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers.set('authorization', `Bearer ${refreshed}`);
        const retry = await fetch(fullUrl, { ...init, headers, credentials: 'include' });
        return parseResponse<T>(retry);
      }
      // Восстановить сессию не вышло.
      this.setAccessToken(null);
      if (this.embedRefreshToken !== null) {
        // Во встроенном режиме уводить некуда: наша страница входа внутри
        // чужой рамки бесполезна. Сообщаем странице, она попросит кабинет
        // выдать новый билет.
        this.embedRefreshToken = null;
        this.onEmbedSessionLost?.();
        return new Promise(() => {});
      }
      localStorage.removeItem('inbx.access_token.v2');
      localStorage.removeItem('inbx.refresh_token.v2');
      localStorage.removeItem('inbx.user.v2');
      window.location.href = '/login';
      return new Promise(() => {});
    }

    return parseResponse<T>(res);
  }

  private async tryRefresh(): Promise<string | null> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      try {
        const refreshToken =
          this.embedRefreshToken ?? localStorage.getItem('inbx.refresh_token.v2');
        if (!refreshToken) return null;
        const res = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!res.ok) return null;
        const body = (await res.json()) as { access_token?: string; refresh_token?: string };
        if (this.embedRefreshToken !== null) {
          if (!body.access_token) return null;
          this.accessToken = body.access_token;
          if (body.refresh_token) this.embedRefreshToken = body.refresh_token;
          return body.access_token;
        }
        if (!body.access_token) return null;
        this.setAccessToken(body.access_token);
        localStorage.setItem('inbx.access_token.v2', body.access_token);
        if (body.refresh_token) localStorage.setItem('inbx.refresh_token.v2', body.refresh_token);
        return body.access_token;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  get<T>(path: string, opts?: { workspaceSlug?: string }): Promise<T> {
    return this.request<T>(path, { method: 'GET' }, opts);
  }

  post<T>(
    path: string,
    body?: unknown,
    opts?: { workspaceSlug?: string; idempotencyKey?: string },
  ): Promise<T> {
    return this.request<T>(
      path,
      {
        method: 'POST',
        body: body == null ? undefined : JSON.stringify(body),
        ...(opts?.idempotencyKey ? { headers: { 'idempotency-key': opts.idempotencyKey } } : {}),
      },
      opts,
    );
  }

  patch<T>(path: string, body?: unknown, opts?: { workspaceSlug?: string }): Promise<T> {
    return this.request<T>(
      path,
      { method: 'PATCH', body: body == null ? undefined : JSON.stringify(body) },
      opts,
    );
  }

  put<T>(path: string, body?: unknown, opts?: { workspaceSlug?: string }): Promise<T> {
    return this.request<T>(
      path,
      { method: 'PUT', body: body == null ? undefined : JSON.stringify(body) },
      opts,
    );
  }

  delete<T>(path: string, opts?: { workspaceSlug?: string }): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' }, opts);
  }

  /** Загрузка файла (multipart). Не ставит json-content-type — boundary задаёт браузер. */
  async upload<T>(path: string, file: File, opts?: { workspaceSlug?: string }): Promise<T> {
    const fd = new FormData();
    fd.append('file', file);
    const headers = new Headers();
    if (this.accessToken) headers.set('authorization', `Bearer ${this.accessToken}`);
    const fullUrl = opts?.workspaceSlug ? `/api/v1/${opts.workspaceSlug}${path}` : `/api${path}`;
    const res = await fetch(fullUrl, { method: 'POST', body: fd, headers, credentials: 'include' });
    return parseResponse<T>(res);
  }
}

async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown = text;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // raw
    }
  }
  if (!res.ok) {
    throw new ApiClientError(
      (body as ApiError)?.error?.code ?? ERROR_CODES.INTERNAL_ERROR,
      (body as ApiError)?.error?.message ?? `HTTP ${res.status}`,
      res.status,
      (body as ApiError)?.error?.details,
    );
  }
  return body as T;
}

export class ApiClientError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
  }
}

export const apiClient = new ApiClient();
